import {
  joinSupplierPartRows,
  type JoinedSupplierPartRow,
} from "./joinSupplierPartRows";
import { PartQuantity, SupplierPart } from "./partsCalculationUtils";

export type PartConfirmationLookup = {
  quantity: number;
  status?: "confirmed" | "declined";
};

export type AddToSupplierBucketOptions = {
  /** View Details already applied user-edited qty; do not recap. */
  skipAssignQtyCap?: boolean;
};

export const confirmationForRow = (
  row: JoinedSupplierPartRow,
  confirmedByPartId: Map<string, PartConfirmationLookup>,
): PartConfirmationLookup | undefined =>
  confirmedByPartId.get(row.partId) || confirmedByPartId.get(row.partNumber);

export const resolvedConfirmedQty = (
  confirmation?: PartConfirmationLookup,
): number | undefined => {
  if (!confirmation) {
    return undefined;
  }
  if (confirmation.status === "declined") {
    return 0;
  }
  if (confirmation.status === "confirmed") {
    return confirmation.quantity ?? 0;
  }
  return undefined;
};

/** Same default/max as View Details: min(confirmed ?? stock, asked). Declined → 0. */
export const assignQtyMaxForRow = (
  row: JoinedSupplierPartRow,
  confirmation?: PartConfirmationLookup,
): number => {
  const askedQty = Math.max(0, Number(row.askedQty) || 0);
  const stockQty = Math.max(0, Number(row.stockQty) || 0);
  const cap = resolvedConfirmedQty(confirmation) ?? stockQty;
  return Math.max(0, Math.min(cap, askedQty));
};

export const buildConfirmedByPartId = (
  parts: Array<{
    partId?: string;
    partNumber?: string;
    quantity?: number;
    status?: "confirmed" | "declined";
  }> = [],
): Map<string, PartConfirmationLookup> => {
  const map = new Map<string, PartConfirmationLookup>();
  parts.forEach((part) => {
    const value = {
      quantity: part.quantity ?? 0,
      status: part.status,
    };
    if (part.partId) {
      map.set(part.partId, value);
    }
    if (part.partNumber) {
      map.set(part.partNumber, value);
    }
  });
  return map;
};

/**
 * Parts View Details would add on open + "Add to part bucket" with defaults:
 * selected when assign qty > 0, quantity = that default.
 */
export const buildDefaultSelectedPartsForBucket = (
  remainingParts: PartQuantity[],
  supplierHits: Array<SupplierPart & { id?: string }>,
  confirmedByPartId: Map<string, PartConfirmationLookup>,
): Array<
  SupplierPart & { id?: string; rootPartNumber: string; familyQuantity: number }
> =>
  joinSupplierPartRows(remainingParts, supplierHits).flatMap((row) => {
    const quantity = assignQtyMaxForRow(
      row,
      confirmationForRow(row, confirmedByPartId),
    );
    if (quantity <= 0) {
      return [];
    }
    return [
      {
        ...row.supplierPart,
        rootPartNumber: row.rootPartNumber,
        familyQuantity: quantity,
      },
    ];
  });
